import { withErrorBoundary } from 'app/components/error-boundary';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { useUser } from 'app/network/user';
import { ListingType } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import React, { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { Platform, View } from 'react-native';
import { LoanFeeModals, useLoanFeeModals } from '../management/loan-fee';

import { images } from 'app/design/images';
import { Gradient } from 'app/design/primitives/gradient';
import { QUICK_NEW_ITEMS_KEY } from 'app/hooks/use-quickoffers';
import { getI18nLanguage } from 'app/lib/language';
import { WhiteLabel } from 'app/types/white-labels';
import { useTheme } from 'app/shopify';

function ThanksAccessory({
  backgroundColor,
  hasListings,
  loanFeeAvailable,
  loanFeeModalsState,
  onAddLoanFeePressed,
  onBackToBoardPressed,
  onViewListingPressed,
}: {
  backgroundColor: string;
  hasListings: boolean;
  loanFeeAvailable: boolean;
  loanFeeModalsState: ReturnType<typeof useLoanFeeModals>;
  onAddLoanFeePressed: () => void;
  onBackToBoardPressed: () => void;
  onViewListingPressed: () => void;
}) {
  const theme = useTheme();
  const { t } = useScopedTranslation();
  const isMutuo = isWhiteLabel(WhiteLabel.Mutuo);

  if (loanFeeAvailable && hasListings) {
    return (
      <Accessory style={{ backgroundColor }}>
        <Button
          marginHorizontal="spacing.general.x4"
          marginTop="spacing.general.x8"
          onPress={onViewListingPressed}
          variant={isMutuo ? 'whiteWithBorder' : 'primary'}
          style={{ bottom: theme.spacing['spacing.general.x2'] }}
        >
          <Text color={isMutuo ? 'text.primary' : 'surface.default'} fontWeight="700" textAlign="center">
            {t('buttonListingsText')}
          </Text>
        </Button>

        <Button
          infoButtonOnPress={() => loanFeeModalsState.setIsLoanFeeModalOpen(true)}
          marginHorizontal="spacing.general.x4"
          marginTop="spacing.general.x3"
          onPress={onAddLoanFeePressed}
          variant={isMutuo ? 'whiteWithBorder' : 'secondary'}
          style={{ bottom: theme.spacing['spacing.general.x2'] }}
        >
          <Text color={isMutuo ? 'text.primary' : undefined} fontWeight="700" textAlign="center">
            {t('buttonAddLoanFeeText')}
          </Text>
        </Button>
      </Accessory>
    );
  }

  return (
    <Accessory style={{ backgroundColor }}>
      <Button
        marginHorizontal="spacing.general.x4"
        onPress={onBackToBoardPressed}
        variant={isMutuo ? 'whiteWithBorder' : 'primary'}
        style={{ bottom: theme.spacing['spacing.general.x2'] }}
      >
        <Text color={isMutuo ? 'text.primary' : 'surface.default'} fontWeight="700" textAlign="center">
          {t('buttonBackText')}
        </Text>
      </Button>

      <Button
        marginHorizontal="spacing.general.x4"
        marginTop="spacing.general.x3"
        onPress={onViewListingPressed}
        variant={isMutuo ? 'whiteWithBorder' : 'secondary'}
        style={{ bottom: theme.spacing['spacing.general.x2'] }}
      >
        <Text color={isMutuo ? 'text.primary' : undefined} fontWeight="700" textAlign="center">
          {t('buttonListingsText')}
        </Text>
      </Button>
    </Accessory>
  );
}

const ThanksScreenComponent: NavigationScreen<'quick-new-thanks'> = ({ route }) => {
  const theme = useTheme();
  const { t } = useScopedTranslation();
  const navigation = useNavigation<'quick-new-thanks'>();
  const { data: user } = useUser();
  const currentLang = getI18nLanguage();

  useNavigationOptions(navigation, {
    headerShown: false,
  });

  useEffect(() => {
    if (Platform.OS === 'web') {
      try {
        sessionStorage.removeItem(QUICK_NEW_ITEMS_KEY);
      } catch {}
    }
  }, []);

  const loanFeeAvailable = isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags);

  const onBackToBoardPressed = () => {
    navigation.reset({ routes: [{ name: 'main' }] });
  };

  const onViewListingPressed = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'main' }],
    });

    requestAnimationFrame(() => {
      navigation.navigate('management', {
        screen: 'my-listings',
        params: { type: ListingType.offer },
      });
    });
  };

  const onAddLoanFeePressed = () => {
    onViewListingPressed();
    setTimeout(
      () =>
        navigation.navigate(
          'loan-fee',
          {
            listingId: route.params!.listingIds!.join(','),
          },
          { pop: true },
        ),
      300,
    );
  };

  const loanFeeModalsState = useLoanFeeModals();
  const hasListings = (route.params?.listingIds?.length ?? 0) > 0;
  const bgColor = isWhiteLabel()
    ? theme.colors['surface.inverse.tertiary-neutral']
    : theme.colors['white-label.splash'];
  const bubbleColor = theme.colors['text.brand'];

  return (
    <Page
      accessory={
        <ThanksAccessory
          backgroundColor={bgColor}
          hasListings={hasListings}
          loanFeeAvailable={loanFeeAvailable}
          loanFeeModalsState={loanFeeModalsState}
          onAddLoanFeePressed={onAddLoanFeePressed}
          onBackToBoardPressed={onBackToBoardPressed}
          onViewListingPressed={onViewListingPressed}
        />
      }
      style={{ backgroundColor: bgColor }}
    >
      <Text
        fontSize={40}
        fontWeight="700"
        marginBottom="spacing.general.x8"
        marginTop="spacing.general.x8"
        marginHorizontal="spacing.general.x8"
        textAlign="center"
        style={isWhiteLabel(WhiteLabel.Mutuo) ? { color: '#FFFFFF' } : undefined}
      >
        {t('headerText')}
      </Text>

      <View style={{ alignItems: 'center' }}>
        <View
          style={{
            backgroundColor: bubbleColor,
            borderRadius: theme.borderRadii['radius.xxl'],
            marginHorizontal: theme.spacing['spacing.general.x4'],
            maxWidth: 460,
            paddingHorizontal: theme.spacing['spacing.general.x8'],
            paddingVertical: theme.spacing['spacing.general.x4'],
            width: '100%',
          }}
        >
          <Text color="surface.default" fontSize={18} fontWeight="700" lineHeight={24} textAlign="center">
            {t('didYouKnowTitle')}
          </Text>
          <Text color="surface.default" fontSize={16} lineHeight={22} marginTop="spacing.general.x1" textAlign="center">
            {t('didYouKnowBody')}
          </Text>
        </View>
      </View>

      <View style={{ alignItems: 'center', marginBottom: theme.spacing['spacing.general.x4'] }}>
        <Gradient
          from={bgColor}
          fromOpacity={1}
          toOpacity={0}
          style={{ position: 'absolute', top: 28, left: 0, right: 0, height: 81, zIndex: 1 }}
          to={bgColor}
        />
        <PCImage
          aspectRatio={1.6}
          source={currentLang === 'en' ? images.navbarEn : images.navbar}
          style={{ width: '100%' }}
        />
      </View>

      <Text
        fontSize={18}
        lineHeight={22}
        marginHorizontal="spacing.general.x8"
        textAlign="center"
        style={isWhiteLabel(WhiteLabel.Mutuo) ? { color: '#FFFFFF' } : undefined}
      >
        <Trans
          components={{
            strong: <Text fontWeight="700" color={isWhiteLabel(WhiteLabel.Mutuo) ? 'white' : 'text.primary'} />,
          }}
          i18nKey="bodyText"
          ns="quick-new-thanks"
        />
      </Text>

      <LoanFeeModals state={loanFeeModalsState} />
    </Page>
  );
};

export const ThanksScreen: NavigationScreen<'quick-new-thanks'> = withErrorBoundary(ThanksScreenComponent);
